import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import simulatorData from '../data/simulatorDataset.json';
import sankeyData from '../data/sankeyDataset.json';

const MainContainer = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 14px;
`;

const PanelCard = styled.div`
  width: 100%;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  padding: 16px;
`;

const InfoCard = styled(PanelCard)`
  padding: 16px;

  h3 {
    margin: 0 0 8px 0;
    font-size: 1.05rem;
    letter-spacing: -0.01em;
  }

  p {
    margin: 0;
    color: var(--muted);
    line-height: 1.55;
  }
`;

const InfoSectionTitle = styled.h4`
  margin: 14px 0 8px 0;
  font-size: 0.95rem;
  letter-spacing: -0.01em;
  color: var(--text);
`;

const InfoList = styled.ul`
  margin: 8px 0 0 0;
  padding-left: 18px;
  color: var(--text);

  li {
    margin: 6px 0;
    line-height: 1.45;
  }

  strong {
    font-weight: 800;
  }
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 620px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ItemButton = styled.button`
  padding: 12px 12px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.7);
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;

  &[data-active='true'] {
    background: rgba(38, 179, 168, 0.14);
    border-color: rgba(38, 179, 168, 0.45);
  }

  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(38, 179, 168, 0.35);
  }

  &:focus-visible {
    outline: 3px solid rgba(38, 179, 168, 0.45);
    outline-offset: 3px;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  color: var(--muted);
  font-weight: 650;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 28px;

  & input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  & .slider {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background-color: rgba(15, 45, 44, 0.25);
    transition: .2s;
    border-radius: 999px;
    border: 1px solid var(--border);
  }

  & .slider:before {
    position: absolute;
    content: "";
    height: 22px;
    width: 22px;
    left: 3px;
    bottom: 2px;
    background-color: var(--surfaceSolid);
    transition: .2s;
    border-radius: 999px;
    box-shadow: 0 6px 18px rgba(12, 17, 20, 0.16);
  }

  input:checked + .slider {
    background-color: rgba(38, 179, 168, 0.28);
  }

  input:checked + .slider:before {
    transform: translateX(18px);
  }
`;

const ToggleLabel = styled.span`
  font-size: 0.95rem;
  color: var(--muted);
`;


function SelectionPanel() {
  const [items, setItems] = useState([]);
  const [toggleState, setToggleState] = useState('partidos');
  const [selectedItem, setSelectedItem] = useState('');

  const districts = useMemo(() => Object.keys(simulatorData || {}), []);
  const parties = useMemo(() => {
    const partySet = new Set();
    for (const district of Object.keys(simulatorData || {})) {
      for (const party of Object.keys(simulatorData[district] || {})) {
        partySet.add(party);
      }
    }
    return Array.from(partySet).sort((a, b) => a.localeCompare(b, 'pt'));
  }, []);

  useEffect(() => {
    const shuffleArray = (array) => {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
    };

    if (toggleState === 'partidos') {
      const shuffledParties = [...parties];
      shuffleArray(shuffledParties);
      setItems(shuffledParties);
    } else {
      const shuffledDistricts = [...districts];
      shuffleArray(shuffledDistricts);
      setItems(shuffledDistricts);
    }

    // Reset the selection when switching mode to avoid mismatched “party vs district” info.
    setSelectedItem('');
  }, [toggleState, parties, districts]);

  const selectionSummary = useMemo(() => {
    if (!selectedItem) return null;

    if (toggleState === 'partidos') {
      let totalDeputies = 0;
      const electedIn = [];
      const wastedIn = [];

      for (const district of districts) {
        const seats = Number(simulatorData?.[district]?.[selectedItem] ?? 0);
        if (seats > 0) {
          electedIn.push({ district, seats });
          totalDeputies += seats;
        } else {
          wastedIn.push({ district });
        }
      }

      electedIn.sort((a, b) => b.seats - a.seats || a.district.localeCompare(b.district, 'pt'));
      wastedIn.sort((a, b) => a.district.localeCompare(b.district, 'pt'));

      return {
        kind: 'party',
        title: selectedItem,
        stats: {
          districtsTotal: districts.length,
          represented: electedIn.length,
          wasted: wastedIn.length,
          totalDeputies,
        },
        representedHighlights: electedIn.slice(0, 6).map((row) => ({
          label: row.district,
          value: `${row.seats} deputado(s)`,
        })),
        wastedHighlights: wastedIn.slice(0, 8).map((row) => ({
          label: row.district,
          value: '0 deputado(s)',
        })),
      };
    }

    // District mode
    const districtData = simulatorData?.[selectedItem] || {};

    const distribution = Object.entries(districtData)
      .map(([party, seats]) => ({ party, seats: Number(seats) }))
      .sort((a, b) => b.seats - a.seats || a.party.localeCompare(b.party, 'pt'));

    const elected = distribution.filter((row) => row.seats > 0);
    const wastedParties = distribution.filter((row) => row.seats === 0);

    const totalSeats = elected.reduce((sum, row) => sum + row.seats, 0);

    // Compute wasted votes using sankeyDataset (district -> party votes),
    // treating any party with 0 seats in the simulator dataset as “wasted” for this district.
    const wastedVotes = (sankeyData?.links || [])
      .filter((link) => link.source === selectedItem)
      .reduce((sum, link) => {
        const party = link.target;
        const seats = Number(simulatorData?.[selectedItem]?.[party] ?? 0);
        const votes = Number(link.value ?? 0);
        return seats > 0 ? sum : sum + votes;
      }, 0);

    return {
      kind: 'district',
      title: selectedItem,
      stats: {
        totalSeats,
        wastedVotes,
        partiesWasted: wastedParties.length,
      },
      electedHighlights: elected.slice(0, 8).map((row) => ({
        label: row.party,
        value: `${row.seats} deputado(s)`,
      })),
    };
  }, [selectedItem, toggleState, districts]);

  return (
    <MainContainer>
      <ToggleRow>
        <ToggleLabel>Partidos</ToggleLabel>
        <ToggleSwitch>
          <input
            type="checkbox"
            onChange={() => setToggleState(toggleState === 'partidos' ? 'circulos' : 'partidos')}
            checked={toggleState !== 'partidos'}
            aria-label="Alternar entre partidos e círculos"
          />
          <span className="slider"></span>
        </ToggleSwitch>
        <ToggleLabel>Círculos</ToggleLabel>
      </ToggleRow>

      <PanelCard>
        <ItemsGrid>
          {items.map((item) => (
            <ItemButton
              key={item}
              type="button"
              data-active={item === selectedItem}
              onClick={() => setSelectedItem(item)}
              aria-pressed={item === selectedItem}
            >
              {item}
            </ItemButton>
          ))}
        </ItemsGrid>
      </PanelCard>

      {selectionSummary && (
        <InfoCard aria-live="polite">
          <h3>
            {selectionSummary.kind === 'party' ? 'Partido' : 'Círculo'}: {selectionSummary.title}
          </h3>

          {selectionSummary.kind === 'party' ? (
            <>
              <p>
                Em 2022, votar em <strong>{selectionSummary.title}</strong> foi um voto com representação em{' '}
                <strong>{selectionSummary.stats.represented}</strong> círculo(s) e um <strong>voto desperdiçado</strong> em{' '}
                <strong>{selectionSummary.stats.wasted}</strong> círculo(s).{' '}
                <a href="#simulador">Abrir simulador</a>
              </p>

              {selectionSummary.wastedHighlights.length > 0 && (
                <>
                  <InfoSectionTitle>Exemplos onde o voto não elegeu (voto desperdiçado)</InfoSectionTitle>
                  <InfoList>
                    {selectionSummary.wastedHighlights.map((row) => (
                      <li key={row.label}>
                        <strong>{row.label}</strong>: {row.value}
                      </li>
                    ))}
                  </InfoList>
                </>
              )}

              {selectionSummary.representedHighlights.length > 0 && (
                <>
                  <InfoSectionTitle>Onde elegeu deputados (top)</InfoSectionTitle>
                  <InfoList>
                    {selectionSummary.representedHighlights.map((row) => (
                      <li key={row.label}>
                        <strong>{row.label}</strong>: {row.value}
                      </li>
                    ))}
                  </InfoList>
                </>
              )}

              {selectionSummary.stats.represented === 0 && (
                <p style={{ marginTop: 12 }}>
                  Este partido não elegeu deputados em nenhum círculo — neste dataset, o voto seria sempre desperdiçado.
                </p>
              )}
            </>
          ) : (
            <>
              <p>
                No círculo <strong>{selectionSummary.title}</strong>, foram eleitos <strong>{selectionSummary.stats.totalSeats}</strong> deputado(s).{' '}
                Votos desperdiçados (partidos sem deputados):{' '}
                <strong>{Number(selectionSummary.stats.wastedVotes || 0).toLocaleString('pt-PT')}</strong>.{' '}
                <a href="#simulador">Abrir simulador</a>
              </p>

              {selectionSummary.electedHighlights.length > 0 && (
                <>
                  <InfoSectionTitle>Deputados eleitos por partido (top)</InfoSectionTitle>
                  <InfoList>
                    {selectionSummary.electedHighlights.map((row) => (
                      <li key={row.label}>
                        <strong>{row.label}</strong>: {row.value}
                      </li>
                    ))}
                  </InfoList>
                </>
              )}
            </>
          )}
        </InfoCard>
      )}
    </MainContainer>
  );
}

export default SelectionPanel;
